/**
 * Single reusable utility for all event_date handling in this dashboard.
 *
 * DynamoDB event_date arrives in three mixed formats:
 *   YYYY-MM-DD HH:MM:SS  →  "2026-05-22 10:15:00"
 *   YYYY-MM-DD           →  "2026-05-22"
 *   DD-MM-YYYY           →  "22-05-2026"
 *
 * Detection rule: if the third dash-segment is 4 digits → DD-MM-YYYY;
 * otherwise the first segment is 4 digits → YYYY-MM-DD (with optional time).
 */
export function normalizeEventDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    const datePart = s.split(' ')[0]; // strip optional HH:MM:SS
    const parts = datePart.split('-');
    if (parts.length < 3) return null;

    const [a, b, c] = parts;
    let day: number, month: number, year: number;

    if (c.length === 4) {
        // DD-MM-YYYY
        day = Number(a); month = Number(b); year = Number(c);
    } else {
        // YYYY-MM-DD
        year = Number(a); month = Number(b); day = Number(c);
    }

    if (!day || !month || !year || year < 1900 || month > 12 || day > 31) return null;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
}

/**
 * Format any DynamoDB date string as DD-MM-YYYY for display.
 * Returns '' when the input is missing or unparseable.
 */
export function formatEventDate(dateStr: string | null | undefined): string {
    const d = normalizeEventDate(dateStr);
    if (!d) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
}

/**
 * Sort comparator: newest date first (descending).
 * Safe for mixed-format strings and null values (nulls sort last).
 */
export function compareEventDatesDesc(
    a: string | null | undefined,
    b: string | null | undefined,
): number {
    const dA = normalizeEventDate(a);
    const dB = normalizeEventDate(b);
    if (!dA && !dB) return 0;
    if (!dA) return 1;
    if (!dB) return -1;
    return dB.getTime() - dA.getTime();
}

/**
 * Sort comparator: oldest date first (ascending).
 */
export function compareEventDatesAsc(
    a: string | null | undefined,
    b: string | null | undefined,
): number {
    return -compareEventDatesDesc(a, b);
}
